import { useMemo, useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";
import useMediaQuery from "@mui/material/useMediaQuery";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import LocationOnIcon from "@mui/icons-material/LocationOn";
// @ts-ignore
import { useTheme } from "@mui/material/styles";
// components
import AppButton from "../../components/ui/AppButton";
import AppDatePickerCustom from "../../components/AppDatePickerCustom";
import SingleGooglePlacesInput from "../../components/SingleGooglePlacesInput";
import MilesAndMoreBanner from "./MilesAndMoreBanner";
import AirportOffersList from "./AirportOffersList";
// helpers
import {
  enrichAirportOffers,
  findMatchingTransportRow,
  isTodayOrFutureDate,
  isValidMilesAndMoreNumber,
  shouldPassTransportDetailsToProvider
} from "./helpers/airlineLandingHelpers";
import { parseAirlineLandingPrefillFromSearch } from "./helpers/parseAirlineLandingPrefillFromSearch";
import { navigateToTransportOption } from "../../common/utils/helpers/transportHelpers/navigateToTransportOption";
// hooks
import { useTransportLanguageRefresh } from "../../common/hooks/useTransportLanguageRefresh";
// actions
import { useActions } from "../../common/hooks/useActions";
import { airlineActionCreators, type AirlineState } from "./airlineModule";

export default function AirlineLanding() {
  const theme = useTheme();
  // Stacked form-over-results layout (mobile/tablet); side-by-side from md up.
  const isCompactLayout = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [errorMessageKeys, setErrorMessageKeys] = useState<string[]>([]);
  // null = /v1/airports still pending; array = fetch finished (used to gate one-time URL prefill).
  const [airportsFetchResult, setAirportsFetchResult] = useState<any[] | null>(null);

  const departureDate = useSelector((state: any) => state.airline.departureDate);
  const arrivalAirport = useSelector((state: any) => state.airline.arrivalAirport);
  const finalDestination = useSelector((state: any) => state.airline.finalDestination);
  const availableAirports = useSelector((state: any) => state.airline.availableAirports);
  const isSearching = useSelector((state: any) => state.airline.isSearching);
  const searchError = useSelector((state: any) => state.airline.searchError);
  const airportOffers = useSelector((state: { airline: AirlineState }) => state.airline.airportOffers);
  const airportOffersError = useSelector((state: { airline: AirlineState }) => state.airline.airportOffersError);
  const transportSearchByType = useSelector((state: any) => state.airline.transportSearchByType);
  const searchTime = useSelector((state: any) => state.airline.searchTime);
  const milesAndMoreNumber = useSelector((state: any) => state.airline.milesAndMoreNumber);
  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);
  const googlePlacesApiKey = useSelector((state: any) => state.global.googlePlacesApiKey);

  // Actions
  const setDepartureDate = useActions(airlineActionCreators?.setDepartureDateAction, []);
  const setArrivalAirport = useActions(airlineActionCreators?.setArrivalAirportAction, []);
  const setFinalDestination = useActions(airlineActionCreators?.setFinalDestinationAction, []);
  const setMilesAndMoreNumber = useActions(airlineActionCreators?.setMilesAndMoreNumberAction, []);
  const getAvailableAirports = useActions(airlineActionCreators?.getAvailableAirportsAction, []);
  const getAirportOffers = useActions(airlineActionCreators?.getAirportOffersAction, []);
  const searchTransfers = useActions(airlineActionCreators?.searchTransfersAction, []);
  const clearTransportSearchResults = useActions(airlineActionCreators?.clearTransportSearchResultsAction, []);
  const setSearchError = useActions(airlineActionCreators?.setSearchErrorAction, []);

  const language = localStorage.getItem("i18nextLng") || "en";

  const hasFetchedAirportsRef = useRef(false);
  const hasAppliedUrlPrefillRef = useRef(false);
  // Post-search scroll: anchor on the OPTIONS column; flag set in onSearch, consumed when isSearching clears.
  const resultsSectionRef = useRef<HTMLDivElement>(null);
  const pendingResultsScrollRef = useRef(false);

  useEffect(() => {
    if (!tenantInfo || hasFetchedAirportsRef.current) {
      return;
    }
    hasFetchedAirportsRef.current = true;

    const fetchAirports = async () => {
      const airports = await getAvailableAirports();
      setAirportsFetchResult(Array.isArray(airports) ? airports : []);
    };

    fetchAirports();
  }, [tenantInfo, getAvailableAirports]);

  // Apply URL prefills once after /v1/airports resolves. Invalid params are ignored; fields stay editable.
  useEffect(() => {
    if (airportsFetchResult === null || hasAppliedUrlPrefillRef.current) {
      return;
    }
    hasAppliedUrlPrefillRef.current = true;

    const { mamid, arrivalDate, airportIata } = parseAirlineLandingPrefillFromSearch(location.search);

    if (arrivalDate && departureDate == null) {
      setDepartureDate(arrivalDate);
    }

    if (mamid && milesAndMoreNumber === "") {
      setMilesAndMoreNumber(mamid);
    }

    if (airportIata && arrivalAirport == null) {
      const matchedAirport = airportsFetchResult.find((airport: any) => airport?.iata_code === airportIata);
      if (matchedAirport) {
        const iataCode = matchedAirport.iata_code || matchedAirport.value || null;
        setArrivalAirport(matchedAirport);
        getAirportOffers(iataCode);
      }
    }
  }, [
    airportsFetchResult,
    location.search,
    departureDate,
    milesAndMoreNumber,
    arrivalAirport,
    setDepartureDate,
    setMilesAndMoreNumber,
    setArrivalAirport,
    getAirportOffers
  ]);

  const areOffersEnabled = useMemo(
    () => Object.values(transportSearchByType || {}).some((typeState: any) => typeState?.isSuccess),
    [transportSearchByType]
  );

  // EDIT stays disabled until every transport type has finished (success or error).
  const canEditAfterSearch = useMemo(() => {
    const typeStates = Object.values(transportSearchByType || {});
    if (typeStates.length === 0) {
      return false;
    }

    return !isSearching && typeStates.every((typeState: any) => !typeState?.isLoading);
  }, [isSearching, transportSearchByType]);

  useTransportLanguageRefresh({
    language,
    navigate,
    pathSuffix: "",
    iataCode: arrivalAirport?.iata_code || arrivalAirport?.value || null,
    onProviderLanguageChange: () => {
      if (areOffersEnabled) {
        searchTransfers(milesAndMoreNumber);
      }
    }
  });

  const handleDateChange = (date: Date | null) => {
    setDepartureDate(date);
  };

  const onSearch = () => {
    const errors: string[] = [];
    const isDepartureDateValid = isTodayOrFutureDate(departureDate);

    const validationRules = [
      { isValid: isDepartureDateValid, errorKey: "airline.departureDateInvalid" },
      { isValid: Boolean(arrivalAirport), errorKey: "airline.arrivalAirportInvalid" },
      { isValid: Boolean(finalDestination), errorKey: "airline.finalDestinationInvalid" },
      { isValid: isValidMilesAndMoreNumber(milesAndMoreNumber), errorKey: "airline.milesAndMore.inputInvalid" }
    ];

    validationRules.forEach(rule => {
      if (!rule.isValid) {
        errors.push(rule.errorKey);
      }
    });

    setErrorMessageKeys(errors);

    if (errors.length > 0) {
      return;
    }

    // Arm scroll-to-results for compact screens once transfer search finishes (see effect below).
    pendingResultsScrollRef.current = true;
    searchTransfers(milesAndMoreNumber);
  };

  const onEdit = () => {
    // Step back from "searched" state while preserving user inputs + airport offers list
    setErrorMessageKeys([]);
    setSearchError(null);
    clearTransportSearchResults();
  };

  const formBoxStyle = { pb: 3 };
  const buttonColor = tenantInfo?.style?.button?.color;
  const landingImage = tenantInfo?.style?.landing_image ?? (tenantInfo?.style as any)?.langing_image;
  const hasOffers = Array.isArray(airportOffers) && airportOffers.length > 0;
  const hasResolvedAirportOffers = airportOffers !== null;
  const showAirportOffersEmptyFeedback =
    Boolean(arrivalAirport) && hasResolvedAirportOffers && !hasOffers && !airportOffersError;

  const enrichedAirportOffers = useMemo(() => {
    return enrichAirportOffers(airportOffers, transportSearchByType);
  }, [airportOffers, transportSearchByType]);

  // True after SEARCH finishes: transport types were requested and isSearching cleared.
  const transportSearchFinished = Object.keys(transportSearchByType || {}).length > 0 && !isSearching;
  // Every type failed or returned no match — show a message in the results column (not the form).
  const showSearchNoResults =
    hasOffers && transportSearchFinished && enrichedAirportOffers.length === 0 && !searchError;

  // Scroll to OPTIONS after user SEARCH on compact layout only. Skips language-change re-search (no pending flag).
  useEffect(() => {
    if (!pendingResultsScrollRef.current || isSearching) {
      return;
    }

    pendingResultsScrollRef.current = false;

    if (!isCompactLayout || !hasOffers) {
      return;
    }

    const resultsSection = resultsSectionRef.current;
    if (typeof resultsSection?.scrollIntoView === "function") {
      resultsSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [isSearching, hasOffers, enrichedAirportOffers, isCompactLayout]);

  const onTransportCardClick = (item: any) => {
    const transportType = item?.type?.toLowerCase();
    const typeState = item?.type ? transportSearchByType?.[item.type] : null;
    const typeOffers = Array.isArray(typeState?.data) ? typeState.data : [];
    const matchedOffer = item?.type ? findMatchingTransportRow(typeOffers, item.type) : undefined;
    if (!matchedOffer) {
      return;
    }

    const finalDestinationValue = finalDestination?.value;

    const navState = {
      meta: {
        source: "airline",
        transportType,
        // API offer type for analytics (e.g. SBB, EUROPCAR) — keep original casing from the API
        offerType: item?.type ?? null
      },
      shared: {
        departureDate: departureDate instanceof Date ? format(departureDate, "yyyy-MM-dd") : null,
        time: searchTime,
        offer_code: matchedOffer?.offer_code || item?.offer_code || null,
        arrivalDestination: {
          placeId: arrivalAirport?.address?.place_id,
          description: "",
          formattedAddress: {
            main_text: arrivalAirport?.label,
            secondary_text: arrivalAirport?.address?.formatted_address
          }
        },
        finalDestination: {
          placeId: finalDestinationValue?.place_id,
          description: finalDestinationValue?.description,
          formattedAddress: finalDestinationValue?.structured_formatting
        },
        milesAndMore: milesAndMoreNumber || "",
        iataCode: arrivalAirport?.iata_code || null
      },
      typeSpecific: {
        details: shouldPassTransportDetailsToProvider(transportType) ? matchedOffer?.details : undefined
      }
    };

    navigateToTransportOption({ item, language, navigate, state: navState });
  };

  const actionButtons = (
    <Box sx={{ display: "flex", gap: 2, height: 45 }}>
      <AppButton
        label={t("airline.editButton")}
        onClick={onEdit}
        variant="contained"
        fontSize="15px"
        dataAttribute="airline-edit"
        disableRipple={true}
        customBackgroundColor={theme.palette.primary.dark}
        disabled={!canEditAfterSearch}
      />
      <AppButton
        label={t("airline.searchButton")}
        onClick={onSearch}
        variant="contained"
        color="success"
        customBackgroundColor={buttonColor || theme?.palette?.success?.main}
        isLoading={isSearching}
        fontSize="15px"
        dataAttribute="airline-search"
        disabled={areOffersEnabled}
      />
    </Box>
  );

  const milesAndMoreBanner = (
    <Box sx={hasOffers ? { pb: 3 } : { pt: 3 }}>
      <MilesAndMoreBanner
        showInput={hasOffers}
        disableEditInput={areOffersEnabled}
        milesAndMoreNumber={milesAndMoreNumber}
        onMilesAndMoreChange={setMilesAndMoreNumber}
      />
    </Box>
  );

  const errorMessagesComponent = (errorMessageKeys.length > 0 || searchError) && (
    <Box sx={{ pb: 2, color: "error.main", fontFamily: "Neutraface2Demi" }}>
      {errorMessageKeys.map((errorKey, index) => (
        <Box
          key={`validation-error-${index}`}
          sx={{
            pl: 1.5,
            textIndent: "-0.65em"
          }}
        >
          &#x2022; {t(errorKey)}
        </Box>
      ))}
      {searchError && <Box>{t(searchError)}</Box>}
    </Box>
  );

  return (
    <Box className="airline-landing" sx={{ flexGrow: 1, py: 4, px: 4 }}>
      <Box mb={2} sx={{ fontFamily: "Neutraface2Demi", textTransform: "uppercase", fontSize: 18 }}>
        {t("airline.title")}
      </Box>

      <Grid container>
        <Grid item xs={12}>
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              margin: "0 auto"
            }}
          >
            <Box
              sx={{
                display: { xs: "flex", md: "grid" },
                flexDirection: { xs: "column", md: "row" },
                gridTemplateColumns: { md: "minmax(0, 1fr) auto minmax(0, 1fr)" },
                alignItems: "stretch",
                minHeight: { md: 400 }
              }}
            >
              {/* Left column: form */}
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ px: { xs: 2, sm: 4 }, py: 4 }}>
                  <Box sx={formBoxStyle}>
                    <Box className="airline-landing__label">{t("airline.departureDate")}</Box>
                    <AppDatePickerCustom
                      selectedDate={departureDate}
                      setSelectedDate={handleDateChange}
                      minDate={new Date()}
                      isDisabled={areOffersEnabled}
                      dataAttribute="airline-date"
                    />
                  </Box>
                  <Box sx={{ pb: 1 }}>
                    <Box className="airline-landing__label">{t("airline.route")}</Box>
                    <Autocomplete
                      disablePortal
                      autoHighlight
                      value={arrivalAirport}
                      onChange={(_event: any, value: any) => {
                        setArrivalAirport(value);
                        const iataCode = value?.iata_code || value?.value || null;
                        getAirportOffers(iataCode);
                      }}
                      options={availableAirports}
                      getOptionLabel={(option: any) => option?.label ?? option?.name ?? ""}
                      isOptionEqualToValue={(option: any, value: any) => option?.value === value?.value}
                      disableClearable
                      disabled={areOffersEnabled}
                      componentsProps={{
                        popupIndicator: { "data-cy": "airline-dropdown" } as any
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          disabled={areOffersEnabled}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <FlightTakeoffIcon fontSize="small" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            )
                          }}
                          inputProps={{
                            ...params.inputProps,
                            "data-cy": "airline-route"
                          }}
                          placeholder={t("airline.arrivalAirport")}
                          variant="outlined"
                          InputLabelProps={{ shrink: false }}
                          sx={{
                            "& .MuiInputBase-root": {
                              height: 59,
                              paddingLeft: "12px"
                            },
                            "& .MuiInputBase-input": {
                              height: "28px",
                              display: "flex",
                              alignItems: "center"
                            },
                            "& .MuiInputBase-input::placeholder": {
                              color: "black"
                            }
                          }}
                        />
                      )}
                    />
                    {Boolean(arrivalAirport) && airportOffersError && (
                      <Box
                        data-cy="airline-airport-offers-error"
                        sx={{ pt: 0.5, color: "error.main", fontFamily: "Neutraface2Demi", fontSize: 14 }}
                      >
                        {t(airportOffersError)}
                      </Box>
                    )}
                    {showAirportOffersEmptyFeedback && (
                      <Box
                        data-cy="airline-airport-offers-empty"
                        sx={{ pt: 0.5, color: "text.secondary", fontFamily: "Neutraface2Book", fontSize: 14 }}
                      >
                        {t("airline.airportOffersEmpty")}
                      </Box>
                    )}
                  </Box>
                  <Box sx={formBoxStyle}>
                    {googlePlacesApiKey && (
                      <SingleGooglePlacesInput
                        value={finalDestination}
                        onChange={(value, action) => setFinalDestination(value)}
                        placeholder={t("airline.finalDestination")}
                        apiKey={googlePlacesApiKey}
                        location={{ lat: 0, lng: 0 }}
                        inputTestId="airline-final-destination"
                        dataCyAttribute="airline-destination"
                        onClear={() => setFinalDestination(null)}
                        leftIcon={<LocationOnIcon fontSize="small" />}
                        disabled={areOffersEnabled}
                      />
                    )}
                  </Box>

                  {/* Order of components is different for offers and no offers (options) */}
                  {hasOffers ? (
                    <>
                      {milesAndMoreBanner}
                      {errorMessagesComponent}
                      {actionButtons}
                    </>
                  ) : (
                    <>
                      {errorMessagesComponent}
                      {actionButtons}
                      {milesAndMoreBanner}
                    </>
                  )}
                </Box>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ my: 4, display: { xs: "none", md: "block" } }} />

              {/* Right column: offers list (fallback to placeholder image) */}
              <Box
                className="airline-landing__right-content"
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "stretch",
                  minWidth: 0
                }}
              >
                <Box
                  ref={resultsSectionRef}
                  className="airline-landing__results"
                  sx={{
                    px: { xs: 2, sm: 4 },
                    py: { xs: 1, sm: 4 },
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: hasOffers ? "stretch" : "center",
                    justifyContent: hasOffers ? "stretch" : "center"
                  }}
                >
                  {hasOffers ? (
                    <>
                      <AirportOffersList
                        offers={enrichedAirportOffers}
                        transportSearchByType={transportSearchByType}
                        areOffersEnabled={areOffersEnabled}
                        onOfferClick={onTransportCardClick}
                      />
                      {showSearchNoResults && (
                        <Box
                          data-cy="airline-search-no-results"
                          sx={{ pt: 1, color: "text.secondary", fontFamily: "Neutraface2Book", fontSize: 14 }}
                        >
                          {t("airline.searchNoResults")}
                        </Box>
                      )}
                    </>
                  ) : landingImage ? (
                    <Box sx={{ paddingTop: { xs: 0, sm: 0, md: "23px" } }}>
                      <img
                        src={landingImage}
                        alt=""
                        style={{
                          width: "100%",
                          height: "100%"
                        }}
                      />
                    </Box>
                  ) : null}
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
